import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Banknote, Eye, Plus, Printer, Receipt, ShieldCheck, Undo2, UserRound } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import type { Pagina } from '@/shared/api/cliente';
import type { CobroDeLaJornada, JornadaDeCaja } from '@/shared/api/tipos';
import { CargandoEnLinea } from '@/shared/ui/Cargando';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { useSesion } from '@/shared/sesion/SesionProvider';
import { Tabla } from '@/shared/ui/Tabla';
import type { Columna } from '@/shared/ui/Tabla';
import type { LucideIcon } from 'lucide-react';
import { VistaPreviaDeImpresion } from '@/shared/ui/VistaPreviaDeImpresion';
import { ModalDeCobro } from './ModalDeCobro';
import { PanelDelCobro } from './PanelDelCobro';
import { PanelDeAnulacion } from './PanelDeAnulacion';
import { PanelDeVerificacion } from './PanelDeVerificacion';
import { nombreDelEstadoDePago, repartoPorConcepto, etiquetaDelConcepto } from './estados';
import { useRecibo } from './useRecibo';

const NOMBRE_DEL_MEDIO: Record<string, string> = {
  efectivo: 'Efectivo',
  transferencia: 'Transferencia',
  tarjeta: 'Tarjeta',
  deposito: 'Depósito',
};

export function PaginaCobros() {
  const [cobrando, setCobrando] = useState(false);
  const [mirando, setMirando] = useState<number | null>(null);
  const [verificando, setVerificando] = useState(false);
  const [anulando, setAnulando] = useState<CobroDeLaJornada | null>(null);
  const recibo = useRecibo();
  const clienteDeConsultas = useQueryClient();

  const { perfil, puede } = useSesion();

  const jornada = useQuery({
    queryKey: ['jornada-de-caja'],
    queryFn: () => api.get<JornadaDeCaja>('/cobros/jornada'),
  });

  const j = jornada.data;
  const cobros = j?.cobros ?? [];

  const comoPagina: Pagina<CobroDeLaJornada> = {
    contenido: cobros,
    pagina: 0,
    tamanio: cobros.length || 1,
    totalElementos: cobros.length,
    totalPaginas: 1,
  };

  const columnas: Columna<CobroDeLaJornada>[] = [
    {
      clave: 'orden',
      encabezado: '#',
      alinear: 'centro',
      clase: 'w-12',

      contenido: (_, i) => (
        <span className="importe text-[11px] font-semibold text-armazon-400">
          {cobros.length - i}
        </span>
      ),
    },
    {
      clave: 'comprobante',
      encabezado: 'Comprobante',
      contenido: (c) => (
        <span className="block min-w-0">
          <span
            className={`importe block truncate text-xs font-semibold ${
              c.estado === 'anulado' ? 'text-armazon-400 line-through' : 'text-armazon-800'
            }`}
          >
            {c.numeroComprobante ?? '—'}
          </span>

          {c.estado === 'anulado' && (
            <span className="text-[10px] font-semibold uppercase text-red-600">
              {nombreDelEstadoDePago(c.estado)}
            </span>
          )}
        </span>
      ),
    },
    {
      clave: 'cliente',
      encabezado: 'Quién pagó',
      contenido: (c) => (
        <span className="block min-w-0">
          <span className="flex items-center gap-1.5">
            <span className="truncate text-xs font-medium uppercase text-armazon-800">
              {c.cliente}
            </span>
            {etiquetaDelConcepto(c.concepto) && (
              <span
                title={c.conceptoNombre}
                className="shrink-0 rounded bg-marca-100 px-1.5 py-px text-[9px] font-bold uppercase text-marca-800"
              >
                {etiquetaDelConcepto(c.concepto)}
              </span>
            )}
          </span>
          <span className="importe block text-[10px] text-armazon-500">{c.identificacion}</span>
        </span>
      ),
    },
    {
      clave: 'facturas',
      encabezado: 'Facturas',
      alinear: 'centro',
      contenido: (c) => (
        <span className="importe text-xs text-armazon-600">{c.cuantasFacturas}</span>
      ),
    },
    {
      clave: 'metodo',
      encabezado: 'Medio',
      contenido: (c) => (
        <span className="rounded-md bg-armazon-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-armazon-600">
          {NOMBRE_DEL_MEDIO[c.metodo] ?? c.metodo}
        </span>
      ),
    },
    {
      clave: 'capital',
      encabezado: 'Servicio',
      alinear: 'derecha',
      contenido: (c) => (
        <span className="importe text-xs text-armazon-600">${c.capital.toFixed(2)}</span>
      ),
    },
    {
      clave: 'interes',
      encabezado: 'Interés',
      alinear: 'derecha',
      contenido: (c) => (
        <span
          className={`importe text-xs ${c.interes > 0 ? 'text-amber-600' : 'text-armazon-300'}`}
        >
          ${c.interes.toFixed(2)}
        </span>
      ),
    },
    {
      clave: 'total',
      encabezado: 'Total',
      alinear: 'derecha',
      contenido: (c) => (
        <span className="importe text-xs font-bold text-armazon-900">${c.total.toFixed(2)}</span>
      ),
    },
    {
      clave: 'acciones',
      encabezado: '',
      alinear: 'derecha',
      clase: 'w-20',
      contenido: (c) => (
        <span className="flex justify-end gap-1">
          <BotonDeFila icono={Eye} titulo="Ver el detalle" alPulsar={() => setMirando(c.id)} />
          <BotonDeFila
            icono={Printer}
            titulo="Imprimir el recibo de 58 mm"
            alPulsar={() => void recibo.abrir(c.id, c.numeroComprobante)}
            ocupado={recibo.preparando === c.id}
          />

          {puede('pagos.anular') && c.estado !== 'anulado' && (
            <BotonDeFila
              icono={Undo2}
              titulo="Anular este cobro"
              alPulsar={() => setAnulando(c)}
              peligro
            />
          )}
        </span>
      ),
    },
  ];

  return (
    <>
      <EncabezadoDePagina
        icono={Banknote}
        titulo="Cobros"
        subtitulo="Recibir el pago de una persona en ventanilla"
        acciones={
          <>

            <button
              type="button"
              onClick={() => setVerificando(true)}
              title="Verificar un recibo"
              aria-label="Verificar un recibo"
              className="grid size-11 place-items-center rounded-xl border border-armazon-200 bg-white text-armazon-500 transition-colors hover:border-marca-300 hover:text-marca-700"
            >
              <ShieldCheck className="size-5" aria-hidden />
            </button>

            <button
              type="button"
              onClick={() => setCobrando(true)}
              title="Registrar cobro"
              aria-label="Registrar cobro"
              className="grid size-11 place-items-center rounded-xl bg-marca-600 text-white shadow-sm transition-colors hover:bg-marca-700"
            >
              <Plus className="size-5" aria-hidden />
            </button>
          </>
        }
      />

      <ResumenDeLaJornada jornada={j} cajero={perfil?.nombre ?? ''} cargando={jornada.isPending} />

      <Tabla
        datos={comoPagina}
        cargando={jornada.isPending}
        columnas={columnas}
        tamanio={comoPagina.tamanio}
        alCambiarPagina={() => undefined}
        alCambiarTamanio={() => undefined}
        paginado={false}
        entidad="cobros"
        iconoVacio={Receipt}
        mensajeVacio="Todavía no ha cobrado nada hoy"
      />

      <PanelDeVerificacion abierto={verificando} alCerrar={() => setVerificando(false)} />

      {anulando && (
        <PanelDeAnulacion
          cobro={anulando}
          abierto
          alCerrar={() => setAnulando(null)}
          alAnular={() => {
            void clienteDeConsultas.invalidateQueries({ queryKey: ['jornada-de-caja'] });

            void clienteDeConsultas.invalidateQueries({ queryKey: ['cobro-deuda'] });
            void clienteDeConsultas.invalidateQueries({ queryKey: ['cartera-resumen'] });
          }}
        />
      )}

      {mirando !== null && (
        <PanelDelCobro
          pagoId={mirando}
          abierto
          alCerrar={() => setMirando(null)}
          alImprimir={() => void recibo.abrir(mirando)}
          imprimiendo={recibo.preparando === mirando}
        />
      )}

      <VistaPreviaDeImpresion
        abierto={recibo.recibo !== null}
        alCerrar={recibo.cerrar}
        titulo="Recibo de cobro"
        subtitulo={
          recibo.recibo?.comprobante
            ? `Nº ${recibo.recibo.comprobante} · Ticket de 58 mm`
            : 'Ticket de 58 mm'
        }
        fichero={recibo.recibo?.fichero ?? null}
        cargando={recibo.preparando !== null}
      />

      {cobrando && (
        <ModalDeCobro
          alCerrar={() => setCobrando(false)}
          alCobrado={(pago) => {
            void clienteDeConsultas.invalidateQueries({ queryKey: ['jornada-de-caja'] });
            setCobrando(false);

            void recibo.abrir(pago.id, pago.numeroComprobante);
          }}
        />
      )}
    </>
  );
}

function ResumenDeLaJornada({
  jornada,
  cajero,
  cargando,
}: {
  jornada: JornadaDeCaja | undefined;
  cajero: string;
  cargando: boolean;
}) {
  const medios = Object.entries(jornada?.porMetodo ?? {});
  const conceptos = repartoPorConcepto(jornada?.conceptos);

  return (
    <div className="mb-5 grid gap-4 sm:grid-cols-[minmax(0,18rem)_1fr]">
      <div className="rounded-2xl bg-armazon-900 p-5 text-white shadow-sm">
        <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-white/60">
          <UserRound className="size-3" aria-hidden />
          <span className="truncate">Caja de {cajero || '—'}</span>
        </p>
        <p className="mt-2 text-[10px] font-semibold uppercase tracking-wide text-white/60">
          Cobrado {enFecha(jornada?.fecha)}
        </p>

        <p className="importe mt-1 text-3xl font-bold">
          {cargando ? '—' : `$${(jornada?.total ?? 0).toFixed(2)}`}
        </p>
        <p className="mt-1 text-[11px] uppercase text-white/60">
          {cargando ? (
            'Consultando la caja…'
          ) : (
            <>
              {jornada?.cuantos ?? 0} {jornada?.cuantos === 1 ? 'cobro' : 'cobros'} · servicio $
              {(jornada?.capital ?? 0).toFixed(2)}
              {(jornada?.interes ?? 0) > 0 && <> · interés ${(jornada?.interes ?? 0).toFixed(2)}</>}
            </>
          )}
        </p>
      </div>

      <div className="rounded-2xl border border-armazon-200 bg-white p-5">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-armazon-500">
          Por medio de pago
        </p>
        {cargando ? (
          <CargandoEnLinea detalle="Obteniendo registros" />
        ) : medios.length === 0 ? (
          <p className="mt-3 text-xs uppercase text-armazon-400">
            Sin movimientos: el arqueo del día está en cero
          </p>
        ) : (
          <ul className="mt-3 flex flex-wrap gap-x-8 gap-y-2">
            {medios.map(([codigo, cuanto]) => (
              <li key={codigo}>
                <span className="block text-[10px] uppercase text-armazon-500">
                  {NOMBRE_DEL_MEDIO[codigo] ?? codigo}
                </span>
                <span className="importe block text-sm font-bold text-armazon-800">
                  ${cuanto.toFixed(2)}
                </span>
              </li>
            ))}
          </ul>
        )}
        {!cargando && conceptos.length > 0 && (
          <ul className="mt-3 flex flex-wrap gap-x-8 gap-y-2 border-t border-armazon-100 pt-3">
            {conceptos.map(([nombre, cuanto]) => (
              <li key={nombre}>
                <span className="block text-[10px] uppercase text-armazon-500">{nombre}</span>
                <span className="importe block text-sm font-bold text-armazon-800">
                  ${cuanto.toFixed(2)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function enFecha(iso: string | undefined): string {
  if (!iso) {
    return 'hoy';
  }
  const hoy = new Date();
  const deHoy = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-${String(
    hoy.getDate(),
  ).padStart(2, '0')}`;

  if (iso === deHoy) {
    return 'hoy';
  }
  const [anio, mes, dia] = iso.split('-');
  return `el ${dia}/${mes}/${anio}`;
}

function BotonDeFila({
  icono: Icono,
  titulo,
  alPulsar,
  ocupado = false,
  peligro = false,
}: {
  icono: LucideIcon;
  titulo: string;
  alPulsar: () => void;
  ocupado?: boolean;

  peligro?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={alPulsar}
      disabled={ocupado}
      title={titulo}
      aria-label={titulo}
      className={`grid size-7 place-items-center rounded-lg text-armazon-400 transition-colors disabled:opacity-40 ${
        peligro ? 'hover:bg-red-50 hover:text-alerta' : 'hover:bg-armazon-100 hover:text-marca-700'
      }`}
    >
      <Icono className="size-4" aria-hidden />
    </button>
  );
}
